import { __assign, __read } from "tslib";
import { useEffect, useMemo, useRef, useState } from 'react';
import { focusWithoutScrolling } from '../../libs/dom-utils';
import { useListeners } from '../../libs/useListeners';
import { createPressEvent } from './utils/create-press-event';
import { isTargetContainsPoint } from './utils/detect-overlap';
import { isCheckableInput, isValidKeyboardEvent } from './utils/keyboard-event';
import { disableTextSelection, restoreTextSelection } from './utils/text-selection';
import { getTouchById, getTouchFromEvent } from './utils/touch-event';
export function usePress(props) {
    var preventFocusOnPress = props.preventFocusOnPress;
    var _a = useListeners(), addListener = _a.addListener, removeAllListeners = _a.removeAllListeners;
    var _b = __read(useState(false), 2), isPressed = _b[0], setPressed = _b[1];
    var cacheRef = useRef({
        currentPointerId: null,
        // Expect that the currentTarget is always exists
        currentPointerTarget: null,
        isPressed: false,
        isPressStarted: false,
    });
    var propsRef = useRef({});
    // Use ref as cache for reuse props inside memo hook.
    propsRef.current = {
        disabled: props.disabled,
        onPressStart: props.onPressStart,
        onPressUp: props.onPressUp,
        onPressEnd: props.onPressEnd,
        onPress: props.onPress,
    };
    var pressProps = useMemo(function () {
        var cache = cacheRef.current;
        var props = {
            onKeyDown: function (event) {
                if (isValidKeyboardEvent(event.nativeEvent)) {
                    // Use preventDefault for all elements except checkbox and radiobox inputs,
                    // because input should trigger onChange after keydown.
                    if (!isCheckableInput(event.target)) {
                        // Use preventDefault for stop document scroll for interactive elements.
                        event.preventDefault();
                    }
                    event.stopPropagation();
                    if (!cache.isPressed && !event.repeat) {
                        triggerPressStart(createPressEvent(event, event.currentTarget, 'keyboard'));
                    }
                }
            },
            // TODO: Register as global listener after keydown.
            onKeyUp: function (event) {
                if (isValidKeyboardEvent(event.nativeEvent) && !event.repeat) {
                    triggerPressUp(createPressEvent(event, event.currentTarget, 'keyboard'));
                    triggerPressEnd(createPressEvent(event, event.currentTarget, 'keyboard'));
                }
            },
        };
        var triggerPressStart = function (event) {
            var _a = propsRef.current, disabled = _a.disabled, onPressStart = _a.onPressStart;
            if (disabled || cache.isPressStarted) {
                return;
            }
            setPressed(true);
            cache.isPressStarted = true;
            onPressStart === null || onPressStart === void 0 ? void 0 : onPressStart(__assign(__assign({}, event), { type: 'pressstart' }));
        };
        var triggerPressUp = function (event) {
            var _a = propsRef.current, disabled = _a.disabled, onPressUp = _a.onPressUp;
            if (disabled) {
                return;
            }
            onPressUp === null || onPressUp === void 0 ? void 0 : onPressUp(__assign(__assign({}, event), { type: 'pressup' }));
        };
        var triggerPressEnd = function (event, triggerOnPress) {
            if (triggerOnPress === void 0) { triggerOnPress = true; }
            var _a = propsRef.current, onPress = _a.onPress, onPressEnd = _a.onPressEnd;
            if (!cache.isPressStarted) {
                return;
            }
            setPressed(false);
            cache.isPressStarted = false;
            onPressEnd === null || onPressEnd === void 0 ? void 0 : onPressEnd(__assign(__assign({}, event), { type: 'pressend' }));
            if (triggerOnPress) {
                onPress === null || onPress === void 0 ? void 0 : onPress(__assign(__assign({}, event), { type: 'press' }));
            }
        };
        var attach = function (target, id) {
            cache.currentPointerTarget = target;
            cache.currentPointerId = id;
            cache.isPressed = true;
            disableTextSelection();
            setPressed(true);
        };
        var detach = function () {
            if (cache.isPressed) {
                cache.isPressed = false;
                restoreTextSelection();
                setPressed(false);
                removeAllListeners();
            }
        };
        if (typeof PointerEvent !== 'undefined') {
            var onPointerMove_1 = function (event) {
                var pointerType = event.pointerType;
                if (isTargetContainsPoint(cache.currentPointerTarget, event)) {
                    triggerPressStart(createPressEvent(event, cache.currentPointerTarget, pointerType));
                }
                else {
                    triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, pointerType), false);
                }
            };
            var onPointerUp_1 = function (event) {
                // Dispose press only if down and up pointer ids are matches.
                if (event.pointerId === cache.currentPointerId) {
                    detach();
                    if (isTargetContainsPoint(cache.currentPointerTarget, event)) {
                        var pointerType = event.pointerType;
                        triggerPressUp(createPressEvent(event, cache.currentPointerTarget, pointerType));
                        triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, pointerType));
                    }
                }
            };
            // Cancel event can be fired while scroll.
            var onPointerCancel_1 = function (event) {
                if (cache.isPressed) {
                    var pointerType = event.pointerType;
                    triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, pointerType), false);
                }
                detach();
            };
            props.onPointerDown = function (event) {
                var disabled = propsRef.current.disabled;
                // Handle only left clicks.
                if (event.button !== 0) {
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                if (!cache.isPressed && !disabled) {
                    if (!preventFocusOnPress) {
                        focusWithoutScrolling(event.currentTarget);
                    }
                    attach(event.currentTarget, event.pointerId);
                    triggerPressStart(createPressEvent(event, event.currentTarget, event.pointerType));
                    addListener(document, 'pointermove', onPointerMove_1, false);
                    addListener(document, 'pointerup', onPointerUp_1, false);
                    addListener(document, 'pointercancel', onPointerCancel_1, false);
                }
            };
        }
        else {
            var onTouchMove_1 = function (event) {
                var touch = getTouchById(event, cache.currentPointerId);
                if (touch) {
                    if (isTargetContainsPoint(cache.currentPointerTarget, touch)) {
                        triggerPressStart(createPressEvent(event, cache.currentPointerTarget, 'touch'));
                    }
                    else {
                        triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, 'touch'), false);
                    }
                }
            };
            var onTouchEnd_1 = function (event) {
                var touch = getTouchById(event, cache.currentPointerId);
                // Dispose press only if down and up pointer ids are matches.
                if ((touch === null || touch === void 0 ? void 0 : touch.identifier) === cache.currentPointerId) {
                    detach();
                    if (isTargetContainsPoint(cache.currentPointerTarget, touch)) {
                        triggerPressUp(createPressEvent(event, cache.currentPointerTarget, 'touch'));
                        triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, 'touch'));
                    }
                }
            };
            // Cancel event can be fired while scroll.
            var onTouchCancel_1 = function (event) {
                if (cache.isPressed) {
                    triggerPressEnd(createPressEvent(event, cache.currentPointerTarget, 'touch'), false);
                }
                detach();
            };
            props.onTouchStart = function (event) {
                var disabled = propsRef.current.disabled;
                event.preventDefault();
                event.stopPropagation();
                var touch = getTouchFromEvent(event.nativeEvent);
                if (touch && !cache.isPressed && !disabled) {
                    if (!preventFocusOnPress) {
                        focusWithoutScrolling(event.currentTarget);
                    }
                    attach(event.currentTarget, touch.identifier);
                    triggerPressStart(createPressEvent(event, event.currentTarget, 'touch'));
                    addListener(document, 'touchmove', onTouchMove_1, false);
                    addListener(document, 'touchend', onTouchEnd_1, false);
                    addListener(document, 'touchcancel', onTouchCancel_1, false);
                }
            };
        }
        return props;
    }, [addListener, preventFocusOnPress, removeAllListeners]);
    useEffect(function () {
        return restoreTextSelection;
    }, []);
    return {
        isPressed: isPressed,
        pressProps: pressProps,
    };
}
